import {
	AutomationTriggerType,
	IAutomationTrigger,
	IEventRegistrationTrigger,
	INewClientAutomationTrigger,
	IResourceSelectorAutomationTrigger,
	IResourceSelectorSettings,
	NewClientType,
	ResourceSelectorId,
} from '../../extViewmodels/sdk/models';

interface AutomationTriggerDisplayConfig {
	label: string;
	pluralNoun?: string;
}

interface ResourceSelectorDisplayConfig {
	label: string;
	/** Plural form of the label for display (e.g., "Birthdays", "House Anniversaries"). If omitted, label is used as-is. */
	pluralLabel?: string;
	/** Lowercase plural noun for contextual prose (e.g., "birthdays", "policy renewals") */
	pluralNoun?: string;
}

export const TRIGGER_TYPE_DISPLAY: Record<AutomationTriggerType, AutomationTriggerDisplayConfig> = {
	[AutomationTriggerType.NewClient]: { label: 'New Client', pluralNoun: 'new clients' },
	[AutomationTriggerType.NewDonor]: { label: 'New Donor', pluralNoun: 'new donors' },
	[AutomationTriggerType.NewLead]: { label: 'New Lead', pluralNoun: 'new leads' },
	[AutomationTriggerType.NewDonationTrigger]: { label: 'New Donation', pluralNoun: 'new donations' },
	[AutomationTriggerType.NewPolicyTrigger]: { label: 'New Policy', pluralNoun: 'new policies' },
	[AutomationTriggerType.NewMatterTrigger]: { label: 'New Matter', pluralNoun: 'new matters' },
	[AutomationTriggerType.MatterStageChangedTrigger]: {
		label: 'Matter Stage Changed',
		pluralNoun: 'matter stage changes',
	},
	[AutomationTriggerType.MatterStatusChangedTrigger]: {
		label: 'Matter Status Changed',
		pluralNoun: 'matter status changes',
	},
	[AutomationTriggerType.ServiceJobCompletedTrigger]: {
		label: 'Completed Service Job',
		pluralNoun: 'completed service jobs',
	},
	[AutomationTriggerType.ResourceSelector]: { label: 'Resource Selector' },
	[AutomationTriggerType.Tag]: { label: 'Tag Triggered' },
	[AutomationTriggerType.Manual]: { label: 'Manual' },
	[AutomationTriggerType.Texting]: { label: 'Texting Campaign' },
	[AutomationTriggerType.Meeting]: { label: 'Upcoming Meeting' },
	[AutomationTriggerType.EventRegistration]: { label: 'Event Registration' },
	[AutomationTriggerType.OpportunityCreated]: { label: 'Opportunity Created', pluralNoun: 'new opportunities' },
};

export const RESOURCE_SELECTOR_DISPLAY: Partial<Record<ResourceSelectorId, ResourceSelectorDisplayConfig>> = {
	[ResourceSelectorId.HappyBirthday]: { label: 'Birthday', pluralLabel: 'Birthdays', pluralNoun: 'birthdays' },
	[ResourceSelectorId.Turning65]: { label: 'Turning 65', pluralNoun: 'birthdays' },
	[ResourceSelectorId.Turning72]: { label: 'Turning 72', pluralNoun: 'birthdays' },
	[ResourceSelectorId.Turning73]: { label: 'Turning 73', pluralNoun: 'birthdays' },
	[ResourceSelectorId.PolicyRenew]: {
		label: 'Policy Renewal',
		pluralLabel: 'Policy Renewals',
		pluralNoun: 'policy renewals',
	},
	[ResourceSelectorId.UpcomingPolicyRenewal]: {
		label: 'Upcoming Policy Renewal',
		pluralLabel: 'Upcoming Policy Renewals',
		pluralNoun: 'upcoming policy renewals',
	},
	[ResourceSelectorId.FinancialReview]: {
		label: 'Client Review',
		pluralLabel: 'Client Reviews',
		pluralNoun: 'client reviews',
	},
	[ResourceSelectorId.HouseAnniversaries]: {
		label: 'House Anniversary',
		pluralLabel: 'House Anniversaries',
		pluralNoun: 'house anniversaries',
	},
	[ResourceSelectorId.NewDonations]: {
		label: 'New Donation',
		pluralLabel: 'New Donations',
		pluralNoun: 'new donations',
	},
	[ResourceSelectorId.NewPolicy]: { label: 'New Policy', pluralLabel: 'New Policies', pluralNoun: 'new policies' },
	[ResourceSelectorId.GenericKeyDates]: {
		label: 'Custom Date',
		pluralLabel: 'Custom Dates',
		pluralNoun: 'custom dates',
	},
	[ResourceSelectorId.NewClientAutomationsOnHold]: { label: 'New Clients' },
	[ResourceSelectorId.NewDonorAutomationsOnHold]: { label: 'New Donors' },
	[ResourceSelectorId.NewLeadAutomationsOnHold]: { label: 'New Leads' },
	[ResourceSelectorId.OpportunityCreatedAutomationsOnHold]: { label: 'New Opportunities' },
	[ResourceSelectorId.NewMatter]: { label: 'New Matter', pluralLabel: 'New Matters', pluralNoun: 'new matters' },
	[ResourceSelectorId.MatterStageChanged]: {
		label: 'Matter Stage Changed',
		pluralLabel: 'Matter Stage Changes',
		pluralNoun: 'matter stage changes',
	},
	[ResourceSelectorId.MatterStatusChanged]: {
		label: 'Matter Status Changed',
		pluralLabel: 'Matter Status Changes',
		pluralNoun: 'matter status changes',
	},
	[ResourceSelectorId.ServiceJobCompleted]: {
		label: 'Completed Service Job',
		pluralLabel: 'Completed Service Jobs',
		pluralNoun: 'completed service jobs',
	},
};

/** Returns the base display label for a trigger type */
export function getTriggerTypeLabel(triggerType: AutomationTriggerType): string {
	return TRIGGER_TYPE_DISPLAY[triggerType]?.label ?? String(triggerType);
}

/** Returns the display label for a resource selector, falling back to account preferences for custom selectors */
export function getResourceSelectorDisplayLabel(
	selectorId: ResourceSelectorId,
	resourceSelectorSettings?: Record<string, IResourceSelectorSettings>
): string {
	const config = RESOURCE_SELECTOR_DISPLAY[selectorId];
	if (config) {
		return config.label;
	}
	return resourceSelectorSettings?.[selectorId]?.displayName ?? String(selectorId);
}

/** Returns "{label} Automation" for a trigger, with NewClient sub-type and EventRegistration special cases */
export function getAutomationTitle(
	trigger?: IAutomationTrigger,
	resourceSelectorSettings?: Record<string, IResourceSelectorSettings>
): string {
	if (!trigger?._type) {
		return 'New Automation';
	}

	const triggerType = trigger._type as AutomationTriggerType;

	// NewClient sub-types
	if (triggerType === AutomationTriggerType.NewClient) {
		const clientTrigger = trigger as INewClientAutomationTrigger;
		if (clientTrigger.clientType?.includes(NewClientType.Commercial)) {
			return 'New Commercial Client Automation';
		}
		if (clientTrigger.clientType?.includes(NewClientType.Personal)) {
			return 'New Personal Client Automation';
		}
		return 'New Client Automation';
	}

	// EventRegistration uses the event name
	if (triggerType === AutomationTriggerType.EventRegistration) {
		const eventTrigger = trigger as IEventRegistrationTrigger;
		if (eventTrigger.eventName) {
			return `${eventTrigger.eventName} Registration Automation`;
		}
		return 'Event Registration Automation';
	}

	// ResourceSelector and NewPolicyTrigger use the resource selector label
	if (
		triggerType === AutomationTriggerType.ResourceSelector ||
		triggerType === AutomationTriggerType.NewPolicyTrigger ||
		triggerType === AutomationTriggerType.NewDonationTrigger
	) {
		const resourceTrigger = trigger as IResourceSelectorAutomationTrigger;
		if (resourceTrigger.resourceSelector) {
			const label = getResourceSelectorDisplayLabel(resourceTrigger.resourceSelector, resourceSelectorSettings);
			return `${label} Automation`;
		}
	}

	return `${getTriggerTypeLabel(triggerType)} Automation`;
}

/** Returns the plural noun for contextual prose (e.g., "for upcoming birthdays") */
export function getAutomationPluralNoun(
	trigger?: IAutomationTrigger,
	resourceSelectorSettings?: Record<string, IResourceSelectorSettings>
): string {
	if (!trigger?._type) {
		return 'unknown';
	}

	const triggerType = trigger._type as AutomationTriggerType;

	// Check resource selector first for ResourceSelector/NewPolicyTrigger/NewDonationTrigger
	if (
		triggerType === AutomationTriggerType.ResourceSelector ||
		triggerType === AutomationTriggerType.NewPolicyTrigger ||
		triggerType === AutomationTriggerType.NewDonationTrigger
	) {
		const resourceTrigger = trigger as IResourceSelectorAutomationTrigger;
		if (resourceTrigger.resourceSelector) {
			const config = RESOURCE_SELECTOR_DISPLAY[resourceTrigger.resourceSelector];
			if (config?.pluralNoun) {
				return config.pluralNoun;
			}
			const customName = resourceSelectorSettings?.[resourceTrigger.resourceSelector]?.displayName;
			return customName?.toLowerCase() ?? 'unknown';
		}
	}

	return TRIGGER_TYPE_DISPLAY[triggerType]?.pluralNoun ?? 'unknown';
}

/** Returns singular or plural display name for a resource selector ID (backward-compatible with industryUtils) */
export function getResourceSelectorIdDisplayName(resourceSelectorId: ResourceSelectorId, plural = false): string {
	const config = RESOURCE_SELECTOR_DISPLAY[resourceSelectorId];
	if (!config) {
		return String(resourceSelectorId);
	}
	if (plural && config.pluralLabel) {
		return config.pluralLabel;
	}
	return config.label;
}
